import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { CalendarClock, ArrowRight, AlertTriangle, MessageCircle } from "lucide-react";

function buildTextMessage(it) {
  const time = it.return_datetime
    ? new Date(it.return_datetime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : "";
  const dayName = it.return_datetime
    ? new Date(it.return_datetime).toLocaleDateString("en-US", { weekday: "long" })
    : "";
  const first = (it.customer_name || "").split(" ")[0] || "there";
  const veh = it.vehicle_label || "your rental";
  if (it.days_until_return < 0) {
    return `Hi ${first}, this is a friendly reminder — your rental (${veh}) was due back on ${dayName}. Please return it or let us know your renewal plan to avoid additional late fees. Thanks!`;
  }
  if (it.days_until_return === 0) {
    return `Hi ${first}, your rental (${veh}) is due back today at ${time}. Renewing? Send your weekly payment and I'll extend it. Otherwise please return on time. Thanks!`;
  }
  if (it.days_until_return === 1) {
    return `Hi ${first}, quick reminder — your rental (${veh}) is due back tomorrow at ${time}. Let me know if you're renewing so I can extend it. Thanks!`;
  }
  return `Hi ${first}, your rental (${veh}) is due back on ${dayName} at ${time}. Renewing? Just reply and send your weekly payment. Thanks!`;
}

function smsHref(phone, body) {
  if (!phone) return null;
  const cleaned = String(phone).replace(/[^0-9+]/g, "");
  const encoded = encodeURIComponent(body);
  // iOS uses ?& and Android uses ?body= — the & format works on both.
  return `sms:${cleaned}&body=${encoded}`;
}

function dueLabel(iso, daysLeft) {
  if (!iso) return { text: "—", tone: "neutral" };
  const d = new Date(iso);
  const dayName = d.toLocaleDateString("en-US", { weekday: "short" });
  const dateBit = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  let text;
  let tone = "neutral";
  if (daysLeft < 0) {
    text = `Overdue · ${dayName} ${dateBit}`;
    tone = "danger";
  } else if (daysLeft === 0) {
    text = `Today · ${time}`;
    tone = "danger";
  } else if (daysLeft === 1) {
    text = `Tomorrow · ${time}`;
    tone = "warning";
  } else if (daysLeft <= 3) {
    text = `${dayName} ${dateBit}`;
    tone = "warning";
  } else {
    text = `${dayName} ${dateBit}`;
    tone = "neutral";
  }
  return { text, tone };
}

const toneClasses = {
  danger: "bg-red-50 border-red-200 text-red-800",
  warning: "bg-amber-50 border-amber-200 text-amber-900",
  neutral: "bg-gray-50 border-gray-200 text-gray-700",
};

export default function RentalsDueList() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/dashboard/rentals-due")
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, items: [] }));
  }, []);

  if (!data) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="text-sm text-gray-500">Loading rentals due…</div>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-5"
      data-testid="rentals-due-widget"
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="overline">Rentals Due</div>
          <h3 className="font-heading text-lg font-semibold mt-0.5">
            Which day each rental is due back
          </h3>
        </div>
        <span
          className="font-heading text-3xl font-bold tabular-nums"
          data-testid="rentals-due-count"
        >
          {data.count}
        </span>
      </div>

      {data.items?.length ? (
        <ul className="divide-y divide-gray-100">
          {data.items.map((it) => {
            const due = dueLabel(it.return_datetime, it.days_until_return);
            const isOverdue = it.days_until_return < 0;
            return (
              <li
                key={it.reservation_id}
                className="py-3 flex items-center justify-between gap-3"
                data-testid={`rentals-due-item-${it.reservation_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">
                    {it.customer_name || "Unknown"}
                    {isOverdue && (
                      <AlertTriangle size={12} className="text-red-500 shrink-0" />
                    )}
                    {it.renewal_pending && (
                      <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200 font-medium">
                        Pending
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {it.vehicle_label || "—"}
                    {it.plate && <span> · {it.plate}</span>}
                  </div>
                </div>
                <span
                  className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md border tabular-nums ${toneClasses[due.tone]}`}
                  data-testid={`rentals-due-when-${it.reservation_id}`}
                >
                  <CalendarClock size={11} /> {due.text}
                </span>
                {it.customer_phone && (isOverdue || it.days_until_return <= 3) && (
                  <a
                    href={smsHref(it.customer_phone, buildTextMessage(it))}
                    data-testid={`rentals-due-text-${it.reservation_id}`}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md border border-gray-900 bg-gray-900 text-white hover:bg-gray-800 transition"
                    title={`Text ${it.customer_name || "customer"}`}
                  >
                    <MessageCircle size={11} /> Text
                  </a>
                )}
                <Link
                  to={`/admin/reservations/${it.reservation_id}`}
                  className="shrink-0 text-gray-400 hover:text-gray-900 transition"
                  data-testid={`rentals-due-open-${it.reservation_id}`}
                  aria-label="Open reservation"
                >
                  <ArrowRight size={14} />
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">No active rentals — the whole fleet is home.</p>
      )}
    </div>
  );
}
